import { IconNames } from '@dixa/lib-shared-habla';

import {
  AutomationNodeBusinessHoursT,
  AutomationNodeCloseT,
  AutomationNodeConditionT,
  AutomationNodeInitT,
  AutomationNodeIntegrationT,
  AutomationNodeQueueT,
  AutomationNodeReplyT,
  AutomationNodeStopT,
  AutomationNodeTagT,
  AutomationNodeThirdPartyIntegrationT,
  AutomationNodeWaitT,
} from '../../../assets/components/automations/components/nodeTypes/types';
import { NodeT } from '../../../assets/fable/flowEditor';

export interface AutomationActionT<TNodeType, TPayload>
  extends NodeT<TNodeType, TPayload> {
  connectionLabel: string;
  id: string;
  isAction: boolean;
  isEditable: boolean;
  isRemovable: boolean;
  nodeType: TNodeType;
  pageX: number;
  pageY: number;
  parentId: null | string;
  payload: TPayload;
}

export enum AutomationNodeTypes {
  INIT = 'INIT',
  QUEUE = 'QUEUE',
  CONDITION = 'CONDITION',
  TAG = 'TAG',
  REPLY = 'REPLY',
  WAIT = 'WAIT',
  STOP = 'STOP',
  CLOSE = 'CLOSE',
  INTEGRATION = 'EXTERNAL_INTEGRATION',
  BUSINESS_HOURS = 'BUSINESS_HOURS',
  THIRD_PARTY_INTEGRATION = 'THIRD_PARTY_INTEGRATION',
  CONTACT_CUSTOM_ATTRIBUTES = 'CONTACT_CUSTOM_FIELDS',
  CONVERSATION_CUSTOM_ATTRIBUTES = 'CONVERSATION_CUSTOM_FIELDS',
  SIDE_CONVERSATION = 'SIDE_CONVERSATION',
  INTERNAL_NOTE = 'INTERNAL_NOTE',
}

export type VariableType = 'string' | 'int' | 'bool';

/**
 * AutomationFlowActionT Payload structures are still unknown Init-node is
 * required Minimum 2 nodes required
 */
export type AutomationFlowActionT =
  | AutomationNodeInitT
  | AutomationNodeQueueT
  | AutomationNodeConditionT
  | AutomationNodeTagT
  | AutomationNodeReplyT
  | AutomationNodeWaitT
  | AutomationNodeStopT
  | AutomationNodeCloseT
  | AutomationNodeIntegrationT
  | AutomationNodeThirdPartyIntegrationT
  | AutomationNodeBusinessHoursT;

export enum AutomationChannel {
  // TEXT
  WIDGETCHAT = 'widgetchat',
  CONTACTFORM = 'contact_form',
  EMAIL = 'email',
  FACEBOOKMESSENGER = 'facebook_messenger',
  WHATSAPP = 'whats_app',
  ADACHAT = 'adachat',
  KINDLYCHAT = 'kindlychat',
  DIGITALGENIUSCHAT = 'digitalgeniuschat',
  SMS = 'sms',
  MESSENGER = 'messenger',
  // VOICE
  PSTNPHONE = 'pstnphone',
  VOICEMAIL = 'voicemail',
  CALLBACK = 'callback',
}
export enum AutomationChannelHumanNames {
  // TEXT
  widgetchat = 'Chat',
  contact_form = 'Contact form',
  email = 'Email',
  facebook_messenger = 'Facebook Messenger',
  whats_app = 'WhatsApp',
  adachat = 'Ada Chat',
  kindlychat = 'Kindly Chat',
  digitalgeniuschat = 'DigitalGenius Chat',
  messenger = 'Messenger',
  // VOICE
  pstnphone = 'Phone',
  voicemail = 'Voicemail',
  callback = 'Callback',
  sms = 'SMS',
}

export enum AutomationTrigger {
  NONE = 'NONE',
  CONVERSATION_ASSIGNED = 'CONVERSATION_ASSIGNED',
  CONVERSATION_ABANDONED = 'CONVERSATION_ABANDONED',
  CONVERSATION_CLOSED = 'CONVERSATION_CLOSED',
  CONVERSATION_ADDED_TO_QUEUE = 'CONVERSATION_ADDED_TO_QUEUE',
  MESSAGE_SEND_BY_AGENT = 'MESSAGE_SEND_BY_AGENT',
  MESSAGE_SEND_BY_USER = 'MESSAGE_SEND_BY_USER',
  CONVERSATION_TAGGED = 'CONVERSATION_TAGGED',
  CONVERSATION_UNTAGGED = 'CONVERSATION_UNTAGGED',
  CONVERSATION_CUSTOM_ATTRIBUTE_UPDATED = 'CONVERSATION_CUSTOM_ATTRIBUTE_UPDATED',
  CONVERSATION_FIRST_MESSAGE_ADDED = 'CONVERSATION_FIRST_MESSAGE_ADDED',
  CONVERSATION_RATING_UPDATED = 'CONVERSATION_RATING_UPDATED',
  // EXTERNAL_TRIGGER aka Macros
  EXTERNAL_TRIGGER = 'EXTERNAL_TRIGGER',
  // EXTERNAL_TRIGGER_MIM is not exposed when creating automations, used internally for Mim tasks
  EXTERNAL_TRIGGER_MIM = 'EXTERNAL_TRIGGER_MIM',
  // EXTERNAL_TRIGGER_MIM_HANDOFF is not exposed when creating automations, used internally for Mim handoffs
  EXTERNAL_TRIGGER_MIM_HANDOFF = 'EXTERNAL_TRIGGER_MIM_HANDOFF',
}

export const AutomationTriggerHumanNames = {
  NONE: 'None',
  CONVERSATION_ASSIGNED: 'Conversation assigned to agent',
  CONVERSATION_ABANDONED: 'Conversation abandoned',
  CONVERSATION_CLOSED: 'Conversation closed',
  CONVERSATION_ADDED_TO_QUEUE: 'Conversation added to queue',
  MESSAGE_SEND_BY_AGENT: 'Message sent by agent',
  MESSAGE_SEND_BY_USER: 'Message sent by customer',
  CONVERSATION_TAGGED: 'Tag added to conversation',
  CONVERSATION_UNTAGGED: 'Tag removed from conversation',
  CONVERSATION_CUSTOM_ATTRIBUTE_UPDATED:
    'Conversation custom attribute updated',
  CONVERSATION_FIRST_MESSAGE_ADDED: 'Conversation created',
  CONVERSATION_RATING_UPDATED: 'Rating submitted',
  // EXTERNAL_TRIGGER aka Macro
  EXTERNAL_TRIGGER: 'Macro',
  // EXTERNAL_TRIGGER_MIM is not exposed when creating automations, used internally for Mim tasks
  EXTERNAL_TRIGGER_MIM: 'Mim Task',
  // EXTERNAL_TRIGGER_MIM_HANDOFF is not exposed when creating automations, used internally for Mim handoffs
  EXTERNAL_TRIGGER_MIM_HANDOFF: 'Mim Handoff',
};

export const AutomationExternalTriggerTypeHumanNames: Record<
  ExternalId['type'],
  string
> = {
  TemplateId: 'Template',
};
export const AutomationExternalTriggerTypeIconNames: Record<
  ExternalId['type'],
  IconNames
> = {
  TemplateId: 'bolt',
};

type AutomationUpdateHistoryItem = {
  readonly updated_by: string;
  readonly updated_at: string;
};

export type Automation = {
  /** The id of the automation. */
  readonly id: string;
  /** The human readable name of the automation. */
  readonly name: string;
  /** The description of the automation. */
  readonly description: string;
  /** Whether the automation is active or not. */
  readonly active: boolean;
  /** The creation date of the automation. */
  readonly created_at: string;
  /** The last updated date of the automation. */
  readonly updated_at: string;
  /** The list of channels currently supported by automation. */
  readonly channels: AutomationChannel[];
  /**
   * The type of triggers to start an automation. EXTERNAL_TRIGGER_MIM should be
   * hidden from the user and therefore can not be included among the enum
   * AutomationTrigger.
   */
  readonly trigger: AutomationTrigger;
  /** Array of updates to the automation. */
  readonly updated_by_history: AutomationUpdateHistoryItem[] | null;
  /**
   * Array of actions belonging to the automation. Visualised as a directed
   * graph with a root and sink nodes.
   */
  readonly actions: AutomationFlowActionT[];
  /** The organisation id that the automation belongs to. */
  readonly org_id?: string;
  /** The parameters to be used as replacement variables. */
  readonly parameters?: {
    id: string;
    description: string;
    type: VariableType;
    isRequired: boolean;
  }[];
  /** The oauth provider id to be used for the automation. */
  readonly oauthProviderId?: string;
  readonly external_ids: ExternalId[];
};

type ExternalId = {
  /** The id of the external entity. */
  readonly id: string;
  /** The type of external system using the automation/macro. */
  readonly type: 'TemplateId';
};

export type AutomationListingT = Omit<Automation, 'actions' | 'channel'>;

/** The available sort keys for automation */
export type AutomationsSortKey = 'name' | 'updated_at';

type MissingVariableError = {
  /** The type of error. */
  readonly type: 'missing_variable';
  /** The id of the variable. */
  readonly variableId: string;
};

type NetworkError = {
  /** The type of error. */
  readonly type: 'network_error';
  /** The resolved url for the error. */
  readonly resolvedUrl: string;
  /** The resolved body for the error. */
  readonly resolvedBody: string;
  /** The resolved headers for the error. */
  readonly headers: { key: string; value: string }[];
  /** The reason for the error, if any. */
  readonly reason?: 'unknown_host' | 'timeout' | 'unable_to_connect';
};

/** The collection representing the result of a test API call. */
export type AutomationIntegrationNodeTest = {
  /** The response of the api call. */
  readonly response: {
    /** The http status of the api call. */
    readonly status: number;
    /** The data of the api call. */
    readonly body: string;
  };
  /** The errors of the api call. */
  readonly errors: (MissingVariableError | NetworkError)[];
};
